import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, FilePlus2, FileText, RefreshCw, X } from 'lucide-react';

import Topbar from '../main/Topbar';
import ProjectSidebar from '../../components/ProjectSidebar';
import { useBoardMenu } from '../../context/BoardMenuContext';
import { ProjectDetail } from '../../utils/commonUtils';
import { ShopFormUtils } from '../../utils/shopCommon';
import {
  getBoardCategoryList,
  type BoardCategory,
} from '../../utils/boardCategory';
import {
  getBoardDetail,
  isBoardCategoryEnabled,
  parseBoardSettings,
  type BoardDetail,
} from '../../utils/board';
import { getPostListPath, getPostMenuId, isQnaBoardCode } from '../../utils/boardMenu';
import { createPost, getPost, getPostViewPath } from '../../utils/post';

interface FormErrors {
  title?: string;
  content?: string;
  categoryCode?: string;
  files?: string;
}

const FormRow: React.FC<{
  label: string;
  required?: boolean;
  hint?: string;
  children: React.ReactNode;
  error?: string;
}> = ({ label, required, hint, children, error }) => (
  <div className="flex flex-col sm:flex-row sm:items-start gap-3 py-3 border-b border-gray-100 last:border-0">
    <div className="w-52 sm:w-56 flex-shrink-0">
      <p className="text-sm font-medium text-gray-700">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </p>
      {hint && <p className="mt-0.5 text-xs text-gray-500">{hint}</p>}
    </div>
    <div className="flex-1 min-w-0">
      {children}
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  </div>
);

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const PostCreate: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { projectCode: projectCodeFromUrl, boardCode: boardCodeFromUrl } = useParams<{
    projectCode: string;
    boardCode: string;
  }>();

  const parentCodeFromQuery = searchParams.get('parentCode')?.trim() || '';

  const { boards } = useBoardMenu();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [isDesktopSidebarOpen, setIsDesktopSidebarOpen] = useState(true);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState('');

  const [projectDetail, setProjectDetail] = useState<ProjectDetail | null>(null);
  const [boardDetail, setBoardDetail] = useState<BoardDetail | null>(null);
  const [isBoardLoading, setIsBoardLoading] = useState(true);
  const [isParentLoading, setIsParentLoading] = useState(false);
  const [parentPost, setParentPost] = useState<{
    code: string;
    title: string;
    content?: string;
    createdAt: string;
    depth?: number;
  } | null>(null);
  const [categories, setCategories] = useState<BoardCategory[]>([]);

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [categoryCode, setCategoryCode] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const menuBoard = boards.find((b) => b.code === boardCodeFromUrl);
  const boardSettings = useMemo(
    () => boardDetail?.settings ?? parseBoardSettings(menuBoard?.settings),
    [boardDetail, menuBoard?.settings]
  );
  const categoryEnabled = boardDetail
    ? boardDetail.settings.category
    : isBoardCategoryEnabled(menuBoard?.settings);
  const maxUploadCount = boardSettings.upload;
  const fileUploadEnabled = maxUploadCount > 0;

  const boardTitle = boardDetail?.name ?? menuBoard?.name ?? boardCodeFromUrl ?? '';
  const listPath =
    projectCodeFromUrl && boardCodeFromUrl
      ? getPostListPath(projectCodeFromUrl, boardCodeFromUrl)
      : '/projects';

  const backPath =
    parentCodeFromQuery && projectCodeFromUrl && boardCodeFromUrl
      ? getPostViewPath(projectCodeFromUrl, boardCodeFromUrl, parentCodeFromQuery)
      : listPath;

  const isReplyCreate = Boolean(parentCodeFromQuery);

  ShopFormUtils.useMobileSidebarResize(setIsMobileSidebarOpen);

  useEffect(() => {
    if (!projectCodeFromUrl || !boardCodeFromUrl) {
      navigate('/projects', { replace: true });
      return;
    }
    const projectInfo = ShopFormUtils.loadProjectDetail();
    if (!projectInfo) {
      navigate('/projects', { replace: true });
      return;
    }
    const detail: ProjectDetail = {
      id: (projectInfo as { id?: string }).id ?? projectInfo.code ?? '',
      ...projectInfo,
    };
    if (detail.code !== projectCodeFromUrl) {
      navigate('/projects', { replace: true });
      return;
    }
    setProjectDetail(detail);
    setActiveMenu(
      isQnaBoardCode(boardCodeFromUrl) ? 'qna' : getPostMenuId(boardCodeFromUrl)
    );
  }, [projectCodeFromUrl, boardCodeFromUrl, navigate]);

  useEffect(() => {
    if (!projectCodeFromUrl || !boardCodeFromUrl) return;

    let cancelled = false;
    setIsBoardLoading(true);
    getBoardDetail(projectCodeFromUrl, boardCodeFromUrl)
      .then((detail) => {
        if (!cancelled) setBoardDetail(detail);
      })
      .catch(() => {
        if (!cancelled) setBoardDetail(null);
      })
      .finally(() => {
        if (!cancelled) setIsBoardLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [projectCodeFromUrl, boardCodeFromUrl]);

  useEffect(() => {
    if (!projectCodeFromUrl || !boardCodeFromUrl || !categoryEnabled) {
      setCategories([]);
      return;
    }

    let cancelled = false;
    getBoardCategoryList(projectCodeFromUrl, boardCodeFromUrl)
      .then((list) => {
        if (!cancelled) setCategories(list.filter((c) => c.status === 'ACTIVE'));
      })
      .catch(() => {
        if (!cancelled) setCategories([]);
      });

    return () => {
      cancelled = true;
    };
  }, [projectCodeFromUrl, boardCodeFromUrl, categoryEnabled]);

  useEffect(() => {
    if (!projectCodeFromUrl || !boardCodeFromUrl || !parentCodeFromQuery) {
      setParentPost(null);
      return;
    }

    let cancelled = false;
    setIsParentLoading(true);
    getPost(projectCodeFromUrl, boardCodeFromUrl, parentCodeFromQuery)
      .then((post) => {
        if (!cancelled) {
          setParentPost({
            code: post.code,
            title: post.title,
            content: post.content,
            createdAt: post.createdAt,
            depth: post.depth,
          });
        }
      })
      .catch(() => {
        if (!cancelled) setParentPost(null);
      })
      .finally(() => {
        if (!cancelled) setIsParentLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [projectCodeFromUrl, boardCodeFromUrl, parentCodeFromQuery]);

  const validate = (): boolean => {
    const next: FormErrors = {};
    const trimmedTitle = title.trim();

    if (!trimmedTitle) next.title = '제목은 필수입니다.';
    else if (trimmedTitle.length > 100) next.title = '제목은 최대 100자까지 입력할 수 있습니다.';

    if (!content.trim()) next.content = '내용은 필수입니다.';

    if (fileUploadEnabled && selectedFiles.length > maxUploadCount) {
      next.files = `첨부 파일은 최대 ${maxUploadCount}개까지 등록할 수 있습니다.`;
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files ?? []);
    if (fileInputRef.current) fileInputRef.current.value = '';

    if (picked.length === 0) return;

    setSelectedFiles((prev) => {
      const merged = [...prev, ...picked];
      if (merged.length > maxUploadCount) {
        setErrors((err) => ({
          ...err,
          files: `첨부 파일은 최대 ${maxUploadCount}개까지 등록할 수 있습니다.`,
        }));
        return merged.slice(0, maxUploadCount);
      }
      setErrors((err) => ({ ...err, files: undefined }));
      return merged;
    });
  };

  const removeFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    setErrors((err) => ({ ...err, files: undefined }));
  };

  const handleReset = () => {
    setTitle('');
    setContent('');
    setCategoryCode('');
    setSelectedFiles([]);
    setErrors({});
    setSubmitError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    if (!validate() || !projectCodeFromUrl || !boardCodeFromUrl) return;

    try {
      setIsSubmitting(true);
      await createPost(
        projectCodeFromUrl,
        boardCodeFromUrl,
        {
          title: title.trim(),
          content,
          ...(categoryCode ? { categoryCode } : {}),
          ...(parentCodeFromQuery ? { parentCode: parentCodeFromQuery } : {}),
        },
        fileUploadEnabled && selectedFiles.length > 0 ? selectedFiles : undefined
      );
      navigate(
        parentCodeFromQuery
          ? getPostViewPath(projectCodeFromUrl, boardCodeFromUrl, parentCodeFromQuery)
          : listPath,
        { replace: true }
      );
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : '게시물 등록에 실패했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!projectDetail) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <p className="text-gray-500">프로젝트 정보를 불러올 수 없습니다.</p>
          <button
            type="button"
            onClick={() => navigate('/projects')}
            className="mt-4 text-blue-600 hover:text-blue-700"
          >
            프로젝트 목록으로 돌아가기
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-50 flex overflow-hidden">
      <ProjectSidebar
        isDesktopSidebarOpen={isDesktopSidebarOpen}
        setIsDesktopSidebarOpen={setIsDesktopSidebarOpen}
        isMobileSidebarOpen={isMobileSidebarOpen}
        setIsMobileSidebarOpen={setIsMobileSidebarOpen}
        activeMenu={activeMenu}
        setActiveMenu={setActiveMenu}
        projectDetail={projectDetail}
      />

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <Topbar
          title={isReplyCreate ? '답글 작성' : '게시물 추가'}
          subtitle={
            boardCodeFromUrl
              ? `${boardTitle} (${boardCodeFromUrl})`
              : isReplyCreate
                ? parentPost && (parentPost.depth ?? 0) > 0
                  ? '선택한 게시물에 답글을 등록합니다'
                  : '원글에 답글을 등록합니다'
                : '새 게시물을 등록합니다'
          }
          isMobileSidebarOpen={isMobileSidebarOpen}
          setIsMobileSidebarOpen={setIsMobileSidebarOpen}
          isProfileDropdownOpen={isProfileDropdownOpen}
          setIsProfileDropdownOpen={setIsProfileDropdownOpen}
          showMobileLogo={true}
          projectDetail={projectDetail}
        />

        <main className="flex-1 p-4 sm:p-6 overflow-y-auto">
          <div className="max-w-4xl mx-auto">
            <div className="mb-6">
              <button
                type="button"
                onClick={() => navigate(backPath)}
                className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft className="w-4 h-4" />
                {isReplyCreate ? '게시물 보기로' : '게시물 목록으로'}
              </button>
            </div>

            {isReplyCreate && (
              <div className="mb-6 bg-slate-50 rounded-lg border border-slate-200 overflow-hidden">
                <div className="px-4 lg:px-6 py-3 border-b border-slate-200">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    {parentPost && (parentPost.depth ?? 0) > 0 ? '답글 대상' : '원글'}
                  </p>
                </div>
                {isParentLoading ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
                  </div>
                ) : parentPost ? (
                  <div className="px-4 lg:px-6 py-4">
                    <h3 className="text-base font-semibold text-gray-900">
                      {parentPost.title || '(제목 없음)'}
                    </h3>
                    <p className="mt-3 text-sm text-gray-700 whitespace-pre-wrap break-words">
                      {parentPost.content || '(내용 없음)'}
                    </p>
                  </div>
                ) : (
                  <p className="px-4 lg:px-6 py-4 text-sm text-amber-700">
                    원글을 불러오지 못했습니다. parentCode를 확인해 주세요.
                  </p>
                )}
              </div>
            )}

            {isBoardLoading ? (
              <div className="flex justify-center py-16">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                {submitError && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                    {submitError}
                  </div>
                )}

                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                  <div className="px-4 lg:px-6 py-4 border-b border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <FileText className="w-5 h-5" />
                      게시물 정보
                    </h2>
                  </div>
                  <div className="px-4 lg:px-6 py-2 space-y-0">
                    {categoryEnabled && (
                      <FormRow label="카테고리" hint="category">
                        <select
                          value={categoryCode}
                          onChange={(e) => {
                            setCategoryCode(e.target.value);
                            setErrors((prev) => ({ ...prev, categoryCode: undefined }));
                          }}
                          className={`block w-full max-w-md px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            errors.categoryCode ? 'border-red-500' : 'border-gray-300'
                          }`}
                        >
                          <option value="">선택 안 함</option>
                          {categories.map((c) => (
                            <option key={c.code} value={c.code}>
                              {c.name}
                            </option>
                          ))}
                        </select>
                      </FormRow>
                    )}

                    <FormRow label="제목" required error={errors.title}>
                      <input
                        type="text"
                        value={title}
                        onChange={(e) => {
                          setTitle(e.target.value);
                          setErrors((prev) => ({ ...prev, title: undefined }));
                        }}
                        placeholder="제목 (최대 100자)"
                        maxLength={100}
                        className={`block w-full min-w-[200px] px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          errors.title ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                    </FormRow>

                    <FormRow label="내용" required error={errors.content}>
                      <textarea
                        value={content}
                        onChange={(e) => {
                          setContent(e.target.value);
                          setErrors((prev) => ({ ...prev, content: undefined }));
                        }}
                        placeholder="게시물 내용을 입력하세요"
                        rows={12}
                        className={`block w-full min-w-[200px] px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y ${
                          errors.content ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                    </FormRow>

                    {fileUploadEnabled && (
                      <FormRow
                        label="첨부 파일"
                        hint={`upload · 최대 ${maxUploadCount}개`}
                        error={errors.files}
                      >
                        <div className="space-y-3">
                          <div className="flex flex-wrap items-center gap-2">
                            <input
                              ref={fileInputRef}
                              type="file"
                              multiple
                              className="hidden"
                              onChange={handleFileChange}
                              disabled={selectedFiles.length >= maxUploadCount}
                            />
                            <button
                              type="button"
                              onClick={() => fileInputRef.current?.click()}
                              disabled={selectedFiles.length >= maxUploadCount}
                              className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <FilePlus2 className="w-4 h-4" />
                              파일 선택
                            </button>
                            <span className="text-xs text-gray-500">
                              {selectedFiles.length} / {maxUploadCount}개
                            </span>
                          </div>

                          {selectedFiles.length > 0 && (
                            <ul className="border border-gray-200 rounded-lg divide-y divide-gray-100">
                              {selectedFiles.map((file, index) => (
                                <li
                                  key={`${file.name}-${file.size}-${index}`}
                                  className="flex items-center justify-between gap-3 px-3 py-2 text-sm"
                                >
                                  <span className="truncate text-gray-800">{file.name}</span>
                                  <span className="flex items-center gap-2 shrink-0 text-gray-500">
                                    <span className="text-xs">{formatFileSize(file.size)}</span>
                                    <button
                                      type="button"
                                      onClick={() => removeFile(index)}
                                      className="p-1 text-gray-400 hover:text-red-600 rounded"
                                      aria-label="첨부 파일 제거"
                                    >
                                      <X className="w-4 h-4" />
                                    </button>
                                  </span>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </FormRow>
                    )}
                  </div>
                </div>

                <div className="flex justify-between items-center mt-6">
                  <button
                    type="button"
                    onClick={handleReset}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center text-sm font-medium text-gray-700"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    초기화
                  </button>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => navigate(backPath)}
                      className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium text-gray-700"
                    >
                      취소
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting || (isReplyCreate && isParentLoading)}
                      className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center text-sm font-medium"
                    >
                      {isSubmitting ? '등록 중...' : isReplyCreate ? '답글 등록' : '게시물 등록'}
                    </button>
                  </div>
                </div>
              </form>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default PostCreate;
